import { z } from 'zod';
import { amountSchema, currencyCodeSchema, dateOnlySchema, exchangeRateSchema, exchangeRateSourceSchema } from './common';
import { documentBaseSchema } from './documents';
import { TAG_MATCH_MODES } from './tags';
import { advancedFilterQueryFragment } from './record-filters';
import { tdsDeductionSchema } from './withholding';
import { patchOf } from './zod-setup';
/** Invoices, estimates and the rest of the sales cycle. */
export const createInvoiceSchema = documentBaseSchema
    .extend({
    customerId: z.string().trim().min(1, 'Pick a customer').max(36),
    /** Absent means "allocate the next number in the series" - the usual case. */
    number: z.string().trim().max(60).optional(),
    dueDate: dateOnlySchema.optional(),
    paymentTermId: z.string().trim().max(64).nullable().optional(),
    /** Issue it immediately instead of saving a draft. */
    markAsSent: z.boolean().optional(),
    /**
     * Brought in from another system (0018): the number it had THERE and
     * where that was. Fint still allocates its own `number`; this prints
     * beside it as "originally INV-2024-017 in Zoho". Unique per book - a
     * second invoice claiming the same external number is a 409 naming the
     * first. `imported` is NOT on this schema: it is set by the import path
     * alone, because it is what lets the Inbox record the invoice as issued
     * without a person pressing Send, and a client must not be able to
     * claim that of an invoice it typed.
     */
    externalNumber: z.string().trim().min(1).max(60).optional(),
    sourceSystem: z.string().trim().min(1).max(80).optional(),
})
    .strict();
/**
 * An edit. On a DRAFT nothing more is asked. On an ISSUED invoice a `reason`
 * is required: the customer holds that figure, and the change goes on the
 * record with what it said before, what it says now, and why - the question
 * a CA asks is never "was it edited" but "why was ₹10,500.01 changed".
 */
// patchOf, not .partial(): an edit never fills in a create-time default (zod 4 - see zod-setup.ts).
export const updateInvoiceSchema = patchOf(createInvoiceSchema)
    .extend({ reason: z.string().trim().max(300).optional() })
    .strict();
export const createEstimateSchema = documentBaseSchema
    .extend({
    customerId: z.string().trim().min(1, 'Pick a customer').max(36),
    number: z.string().trim().max(60).optional(),
    /** After this the quote lapses. Shown to the customer in the portal. */
    expiryDate: dateOnlySchema.optional(),
})
    .strict();
export const updateEstimateSchema = patchOf(createEstimateSchema).strict();
/** Estimates get their own lifecycle - a customer accepts or declines them. */
export const ESTIMATE_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired', 'invoiced'];
export const listInvoicesQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    customerId: z.string().trim().max(36).optional(),
    status: z
        .enum(['all', 'draft', 'sent', 'overdue', 'partially_paid', 'paid', 'unpaid', 'void', 'written_off'])
        .default('all'),
    /**
     * A project id, or the literal `none` for "not on a project".
     *
     * The same sentinel `listExpensesQuerySchema` uses, and deliberately not a
     * second convention: "not on a project" is a real question - it is what
     * tells somebody adopting projects that their coverage is 23% - and one
     * screen spelling it `none` while another spelt it `unassigned` would make
     * a pasted URL mean different things on two tabs of the same app.
     */
    projectId: z.string().trim().max(36).optional(),
    /**
     * Repeatable. `?tag=a&tag=b` arrives as an array from Express; a single
     * `?tag=a` arrives as a string, so both are accepted and normalised here
     * rather than in the controller.
     */
    tag: z
        .union([z.string().trim().max(36), z.array(z.string().trim().max(36))])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .pipe(z.array(z.string().min(1)).max(10))
        .optional(),
    tagMatch: z.enum(TAG_MATCH_MODES).default('any'),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    sort: z.enum(['date', 'number', 'customer', 'total', 'dueDate', 'balance']).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    // `where` / `savedFilterId`: the condition builder, on top of the
    // named filters above. See record-filters.ts.
    ...advancedFilterQueryFragment,
})
    .strict();
// ── Payments received ─────────────────────────────────────────────────────
export const PAYMENT_MODES = ['cash', 'bank_transfer', 'cheque', 'card', 'upi', 'online', 'other'];
/**
 * One payment, allocated across one or more invoices.
 *
 * Allocation is explicit rather than "oldest first", because a customer paying
 * a specific invoice and a customer paying down a balance are different events
 * and only they know which one it was. The unallocated remainder becomes credit
 * on their account rather than being refused - a customer who overpays by ₹100
 * should not have their payment rejected.
 */
export const recordPaymentSchema = z
    .object({
    customerId: z.string().trim().min(1, 'Pick a customer').max(36),
    date: dateOnlySchema,
    /** What the customer paid, in the payment currency, net of anything deducted in transit. */
    amount: amountSchema,
    /**
     * The payment currency, which is the currency of every invoice it
     * settles. Absent means the customer's, else the book's base. A customer
     * with a £ invoice and a ₹ invoice pays them as two payments.
     */
    currency: currencyCodeSchema.optional(),
    /**
     * What the base-currency account was credited - the figure on the bank
     * statement. For a payment in another currency, either this or
     * `exchangeRate` is required; when both arrive the rate is re-derived
     * from the two amounts, so a stale client-side rate cannot disagree
     * with the cash. Ignored on a base-currency payment.
     */
    baseAmount: amountSchema.optional(),
    /** Base units per ONE unit of the payment currency. See `baseAmount`. */
    exchangeRate: exchangeRateSchema.optional(),
    exchangeRateSource: exchangeRateSourceSchema.optional(),
    /** The bank's inward-remittance advice (FIRA / FIRC) number. */
    remittanceReference: z.string().trim().max(120).optional(),
    mode: z.enum(PAYMENT_MODES).default('bank_transfer'),
    /** Which account the money landed in. Drives the bank-account balance. */
    depositToAccountId: z.string().trim().max(36).nullable().optional(),
    referenceNumber: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(2000).optional(),
    /** Bank charges deducted in transit, so the invoice still settles in full. */
    bankCharges: amountSchema.optional(),
    /**
     * TDS the customer deducted before paying.
     *
     * Together with `bankCharges` this is what lets an invoice settle in full
     * on less cash: the allocations may exceed `amount` by exactly these two,
     * because the difference did not vanish - it went to the government and to
     * the bank respectively.
     */
    tds: tdsDeductionSchema.optional(),
    allocations: z
        .array(z
        .object({
        invoiceId: z.string().trim().min(1).max(36),
        amount: amountSchema,
    })
        .strict())
        .default([]),
    /** Email the customer a receipt. */
    sendReceipt: z.boolean().optional(),
})
    .strict();
/**
 * Correct a payment already recorded: the account the money landed in, or
 * the amount the bank actually credited.
 *
 * Narrow on purpose. Those two are what a reconciliation turns up - four
 * payments recorded with no account, one recorded ₹30 short of what HDFC
 * shows - and before this the only correction was to delete the payment and
 * record it again, which issues a new PAY- number and leaves a gap in the
 * series nobody can explain. The customer, the date and the invoices a
 * payment settles are what it IS; changing those is a different payment.
 *
 * `reason` is required for the reason an issued invoice's edit requires one:
 * the customer may hold a receipt for this figure, and the trail says why it
 * moved, in the person's words, beside what changed.
 */
/**
 * Several invoices' PDFs in one download - a month for the accountant - as
 * one PDF with every invoice in turn, or a ZIP with one PDF each. A hundred at
 * most: each is rendered on request, and a year's invoices is a download that
 * should be asked for in months.
 */
export const BULK_PDF_MAX = 100;
export const bulkInvoicePdfSchema = z
    .object({
    ids: z
        .array(z.string().trim().min(1).max(36))
        .min(1, 'Select at least one invoice')
        .max(BULK_PDF_MAX, `At most ${BULK_PDF_MAX} invoices in one download - take a month at a time`),
    format: z.enum(['pdf', 'zip']),
})
    .strict();
export const updatePaymentSchema = z
    .object({
    depositToAccountId: z.string().trim().min(1).max(36).optional(),
    amount: amountSchema.optional(),
    /** The day the bank shows - a UPI credit booked against the day it was promised. */
    date: dateOnlySchema.optional(),
    /** The bank's reference, as the statement prints it. */
    referenceNumber: z.string().trim().max(120).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    reason: z.string().trim().min(3, 'Say why this payment is being corrected').max(500),
})
    .strict()
    .refine((v) => Object.keys(v).some((k) => k !== 'reason'), {
    message: 'Change at least one thing - the account, the amount, the date, the reference or the notes',
});
/**
 * Listing payments received.
 *
 * A schema at all, rather than a bare `customerId` string, because the route
 * had neither a limit nor a cursor: the service capped at a hardcoded 100 rows
 * and there was no way to ask for the 101st, or even to know one existed.
 */
export const listPaymentsQuerySchema = z
    .object({
    customerId: z.string().trim().max(36).optional(),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    ...advancedFilterQueryFragment,
})
    .strict();
/**
 * Money received WITHOUT an invoice - the deposits the reports count as direct
 * income. Listed beside invoice payments so "Payments received" is every rupee
 * that came in, not only the ones a document explains.
 */
export const listDirectReceiptsQuerySchema = z
    .object({
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
// ── Credit notes ──────────────────────────────────────────────────────────
export const createCreditNoteSchema = documentBaseSchema
    .extend({
    customerId: z.string().trim().min(1).max(36),
    number: z.string().trim().max(60).optional(),
    /** The invoice being credited, when it is a straight reversal. */
    againstInvoiceId: z.string().trim().max(36).nullable().optional(),
    reason: z.string().trim().max(300).optional(),
})
    .strict();
// ── Recurring invoices ────────────────────────────────────────────────────
export const RECURRENCE_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'];
export const createRecurringInvoiceSchema = documentBaseSchema
    .omit({ date: true })
    .extend({
    customerId: z.string().trim().min(1).max(36),
    profileName: z.string().trim().min(1, 'Name this schedule').max(160),
    frequency: z.enum(RECURRENCE_FREQUENCIES),
    /** Every N periods. 2 with `monthly` is every other month. */
    interval: z.number().int().min(1).max(52).default(1),
    startDate: dateOnlySchema,
    /** Absent means it runs until someone stops it. */
    endDate: dateOnlySchema.optional(),
    /** Days after generation that each invoice falls due. */
    netDays: z.number().int().min(0).max(365).default(0),
    /** Issue and email each one automatically, rather than leaving a draft. */
    autoSend: z.boolean().default(false),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.endDate && v.endDate < v.startDate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'The end date is before the start date.' });
    }
});
/** Email the invoice PDF: to the customer's address unless another is given, with a line of the sender's own above the standard text. */
export const emailInvoiceSchema = z
    .object({
    to: z.string().trim().email('That is not an email address').max(254).optional(),
    message: z.string().trim().max(2000).optional(),
})
    .strict();
/**
 * Write off what is left on an invoice - the whole balance unless a smaller
 * figure is given. 'rounding' is the paise a customer rounded away;
 * 'bad_debt' is money given up on; 'other' says why in the note.
 */
export const WRITE_OFF_REASONS = ['rounding', 'bad_debt', 'other'];
export const writeOffSchema = z
    .object({
    amount: amountSchema.optional(),
    reason: z.enum(WRITE_OFF_REASONS),
    note: z.string().trim().max(500).optional(),
})
    .strict();
/**
 * When an issued invoice's money is really expected (0039) - Zoho Books'
 * "Expected Payment Date" and its "don't remind until then". Changes the
 * forecast and the reminders, never the invoice, the ledger or a return:
 *   on_terms      as the due date says (the default; clears the rest)
 *   expected      on `date`, which the forecast uses instead of guessing
 *   not_expected  owed, but not counted on: out of the forecast altogether
 * `pauseReminders` holds the reminders to the customer until `date`, or
 * until changed when the money is not expected.
 */
export const PAYMENT_EXPECTATIONS = ['on_terms', 'expected', 'not_expected'];
export const paymentExpectationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('on_terms') }).strict(),
    z
        .object({
        kind: z.literal('expected'),
        date: dateOnlySchema,
        note: z.string().trim().max(300).optional(),
        pauseReminders: z.boolean().default(true),
    })
        .strict(),
    z
        .object({
        kind: z.literal('not_expected'),
        note: z.string().trim().max(300).optional(),
        pauseReminders: z.boolean().default(true),
    })
        .strict(),
]);
/** The three columns read back as one of the three kinds. */
export function paymentExpectationOf(row) {
    if (row.paymentNotExpected)
        return 'not_expected';
    return row.expectedPaymentOn ? 'expected' : 'on_terms';
}
/**
 * Moved off "now" by the person: not expected at all, or expected on a date
 * still to come. Such an invoice is still owed, but it is not overdue against
 * what the customer said, so no dashboard counts it as money late today -
 * it shows as "moved to later" instead (as Zoho Books keeps it out of the
 * current month once an expected date is set).
 */
export function isRescheduled(row, today) {
    if (row.paymentNotExpected)
        return true;
    return !!row.expectedPaymentOn && row.expectedPaymentOn > today;
}
/**
 * TDS on a payment already recorded - the customer's certificate arrived
 * after the deposit was matched, or the match was made without it. The
 * deduction is applied to the invoices the payment settles, oldest first,
 * up to what each still owes; it is not credit.
 */
export const paymentTdsSchema = z
    .object({ tds: tdsDeductionSchema })
    .strict();
//# sourceMappingURL=invoices.js.map